import { useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { MemoryPanel } from "./MemoryPanel";
import { MotionChoice } from "./MotionChoice";
import { PersonalKey } from "./PersonalKey";
import { useMemory } from "./useMemory";

/**
 * My profile (ui-direction screen 7), visible only to its owner: the person's memory with its four concept levels,
 * the onboarding progress, the explanation and batch preferences, the things to remember (which can be removed), an
 * optional model key of one's own, and the motion switch.
 */
export function ProfilePage() {
  const memory = useMemory();
  const [confirmClear, setConfirmClear] = useState(false);

  const clear = async () => {
    if (await memory.clear()) setConfirmClear(false);
  };

  return (
    <div className="ra-page stack">
      <header className="ra-page__head">
        <h1>My profile</h1>
        <p className="muted">What the agent remembers about you, how you want things explained, and your own model key if you use one. Nobody else can see your memory, not even the admin.</p>
        <p>
          <Link to="/change-password" state={{ from: "/profile" }}>
            Change my password
          </Link>
        </p>
      </header>

      {memory.owner === "none" && (
        <EmptyState title="Choose a discipline to see your memory">
          What the agent remembers about you is kept for each discipline, and does not travel from one to another. Choose a discipline at the top of the page. Your model key and the motion switch below do not depend on it.
        </EmptyState>
      )}
      {memory.loading && <Loading>Reading your memory</Loading>}
      {memory.loadError && (
        <Card role="alert">
          <p className="form-error">{memory.loadError}</p>
          <Button onClick={memory.reload}>Try again</Button>
        </Card>
      )}
      {memory.memory && (
        <>
          <MemoryPanel memory={memory.memory} editable disabled={memory.saving} onEdit={(change) => void memory.edit(change)} />
          {memory.problem && (
            <p role="alert" className="form-error">
              {memory.problem}
            </p>
          )}
          {memory.canClear && (
            <div className="row">
              <Button onClick={() => setConfirmClear(true)} disabled={memory.saving}>
                Clear my memory
              </Button>
              <span className="muted">The agent then starts from nothing: it will explain the words again.</span>
            </div>
          )}
        </>
      )}

      <PersonalKey />
      <MotionChoice />

      <ConfirmDialog
        open={confirmClear}
        title="Clear everything the agent remembers about you?"
        message="Your answers, your progress in getting started, your levels and your preferences are removed. Your work in the discipline is not touched."
        confirmLabel="Clear my memory"
        cancelLabel="Keep it"
        busy={memory.saving}
        error={memory.problem}
        onConfirm={() => void clear()}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}
